// [AUTH_GOOGLE_V1] Utility Helpers for PWA and Auth Stability

export const isIOS = () => {
    if (typeof window === 'undefined') return false;
    const isStandardIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    // Check for iPadOS (MacIntel + touchpoints)
    const isIPadOS = (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    return isStandardIOS || isIPadOS;
};

export const isPWA = () => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone || document.referrer.includes('android-app://');
};

// --- storageSafe Logic ---

export const safeSet = (key, val) => {
    try {
        if (typeof localStorage !== 'undefined') {
            localStorage.setItem(key, val);
        } else if (typeof sessionStorage !== 'undefined') {
            sessionStorage.setItem(key, val);
        }
    } catch (e) {
        console.warn("[AUTH_GOOGLE] safeSet failed", e);
    }
};

export const safeGet = (key) => {
    try {
        if (typeof localStorage !== 'undefined') {
            return localStorage.getItem(key);
        } else if (typeof sessionStorage !== 'undefined') {
            return sessionStorage.getItem(key);
        }
    } catch (e) {
        return null;
    }
    return null;
};

export const safeRemove = (key) => {
    try {
        if (typeof localStorage !== 'undefined') {
            localStorage.removeItem(key);
        }
        if (typeof sessionStorage !== 'undefined') {
            sessionStorage.removeItem(key);
        }
    } catch (e) {
        // no-op
    }
};

// --- TTL Logic ---

export const setLocalWithTTL = (key, value, ttlMs) => {
    const item = {
        value: value,
        expiry: Date.now() + ttlMs,
    };
    safeSet(key, JSON.stringify(item));
};

export const getLocalWithTTL = (key) => {
    const itemStr = safeGet(key);
    if (!itemStr) return null;
    try {
        const item = JSON.parse(itemStr);
        if (Date.now() > item.expiry) {
            safeRemove(key);
            return null;
        }
        return item.value;
    } catch (e) {
        safeRemove(key);
        return null;
    }
};

// --- Auth Logs Ring Buffer ---

export const logAuth = (msg, data = null) => {
    const timestamp = new Date().toISOString();
    const logEntry = { ts: timestamp, msg: msg, data: data };

    // Add to window buffer (volatile)
    if (typeof window !== 'undefined') {
        if (!window._AUTH_LOGS_) window._AUTH_LOGS_ = [];
        window._AUTH_LOGS_.push(logEntry);
        if (window._AUTH_LOGS_.length > 50) window._AUTH_LOGS_.shift();
    }

    // Persist to localStorage with TTL 30min
    try {
        const currentLogsStr = safeGet("authDebugLogs");
        let currentLogs = [];
        if (currentLogsStr) {
            const parsed = JSON.parse(currentLogsStr);
            // Check expiry manually or via helper
            if (Date.now() < parsed.expiry) {
                currentLogs = parsed.value;
            }
        }
        currentLogs.push(logEntry);
        if (currentLogs.length > 50) currentLogs.shift();

        setLocalWithTTL("authDebugLogs", currentLogs, 30 * 60 * 1000); // 30 min
    } catch (e) {
        // Silent fail
    }

    //console.log(`[AUTH_GOOGLE] ${msg}`, data || "");
};

/**
 * [V2.0.0] Shared Validation Utilities
 */

export const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
};

/**
 * Normalizes phone numbers to E.164 format
 * @param {string} input - Raw phone input
 * @param {string} countryPhoneCode - Default country code (e.g. "33")
 */
export const normalizePhoneToE164 = (input, countryPhoneCode) => {
    if (!input) return null;
    let clean = String(input).replace(/[\s.-]/g, "");
    
    if (clean.startsWith("00")) clean = "+" + clean.substring(2);

    // Special handling for FR (33)
    if (countryPhoneCode === "33" && clean.startsWith("0") && clean.length === 10) {
        clean = "+33" + clean.substring(1);
    } else if (!clean.startsWith("+")) {
        clean = "+" + countryPhoneCode + clean;
    }

    // Strict E.164 Regex validation
    const e164Regex = /^\+[1-9]\d{7,14}$/;
    return e164Regex.test(clean) ? clean : null;
};
